import useResponsive from 'hooks/useResponsive';
import useTranslation from 'hooks/useTranslation';
import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addProductCart, setAddToCart } from 'actions/ui';
import { Swiper, SwiperSlide } from 'swiper/react';
import {
  Autoplay,
  FreeMode,
  HashNavigation,
  Navigation,
  Pagination,
  Thumbs,
  Zoom,
} from 'swiper';
import { getFileAddress } from '../../../tools/utils';
import Button from '../../utils/Button';
import Icon from '../../utils/Icon';
import Table from '../../utils/table';
import currencyConversion from '../../../tools/constants/currency_conversion.json';
import request from '../../../tools/request';
import { getProducts, getSizes, getCMSDetail } from 'actions/product';
import { Link } from 'components/utils/Link';
import Breadcrumb from '../../generic/Breadcrumb';
import Modal from '../../utils/Modal';
import useShareOnSocial from '../../../hooks/useShareOnSocial';
import BannerShipping from '../list/BannerShipping';
import TagManager from 'react-gtm-module';
import { isIOS, isSafari } from 'react-device-detect';
import { getLocalStorage, setLocalStorage } from '../../../tools/localStorage';
import Accordion from '../../utils/Accordion';
import Counter from './Counter';
import { InlineIcon } from '@iconify/react';
import style from './index.style';
import VariantSelector from '../variant-selector';
import useUserCashback from '../../../hooks/useUserCashback';
import SizeSelector from './SizeSelector';
import Tooltip from '../../utils/Tooltip';
import { useRouterWithLang } from 'hooks/useRouterWithLang';
import Timer from './Timer';

export default function Details({ product: _product, isPublicDetail = false }) {
  const { TRANSLATIONS, translationsParameters } = useTranslation();
  const isLoggedIn = useSelector((s) => s.isLoggedIn);
  const router = useRouterWithLang();
  const { id } = router.query;
  const dispatch = useDispatch();
  const { isMobile, isDesktop, isBigDesktop, isTablet, isLaptop } =
    useResponsive();
  const isSmallDevice = isMobile || isTablet;
  const previousRoute = useSelector((state) => state.previousRoute); // catalog o list con catalogId
  const PERMISSIONS = useSelector((state) => state.permissions);
  const user = useSelector((state) => state?.user);
  const [open, setOpen] = useState(true);
  const refReadMore = useRef();

  const handleOpen = useCallback(() => {
    setOpen((prevState) => !prevState);
  }, []);
  const [openAvailability, setOpenAvailability] = useState(false);
  const handleOpenAvailability = useCallback(() => {
    setOpenAvailability((prevState) => !prevState);
  }, []);
  const [sizeWarning, setSizeWarning] = useState(false);
  const selectModelRef = useRef(null);
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const description = useSelector((state) => state?.description);
  const [lang, setLang] = useState();
  const userCatalogs = useSelector((state) => state?.user?.userCatalogs);
  const cartProducts = useSelector((state) => state.cartProducts);
  const [product, setProduct] = useState(null);
  const outOfStock = product?.availability === 0;
  const isList = previousRoute?.type === 'list';
  const catalog = useMemo(
    () =>
      userCatalogs?.find(
        (userCatalog) => userCatalog._id === previousRoute?.catalogId
      ) || {},
    [userCatalogs, previousRoute?.catalogId]
  );
  const isCatalog = previousRoute?.type === 'catalog';
  const currency = catalog?.currency || 'EUR';
  const currencySymbol = currencyConversion[currency]?.symbol || '€';
  const [relatedSubcategoryItems, setRelatedSubcategoryItems] = useState([]);
  const [relatedBrandItems, setRelatedBrandItems] = useState([]);
  const [modalFreeOpen, setModalFreeOpen] = useState(false);
  const [redirectItem, setRedirectItem] = useState(null);
  const [tags, setTags] = useState([]);
  const { shareOnSocial } = useShareOnSocial({ product, catalog });
  const [sizesTable, setSizesTable] = useState([]);
  const [sizesTableOpen, setSizesTableOpen] = useState(false);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const [locale, setLocale] = useState('');
  const referralCode = useSelector((state) => state.referralCode);
  const refContent = useRef();

  const [productBlocks, setProductBlocks] = useState([]);
  useEffect(() => {
    if (product?.code) {
      (async () => {
        const response = await dispatch(getCMSDetail(product.code));
        const _productBlocks =
          response.data?.items?.[0]?.body?.products?.[product.code];
        if (_productBlocks?.length) {
          setProductBlocks(_productBlocks);
        }
      })();
    }
  }, [dispatch, product?.code]);

  useEffect(() => {
    if (router.isReady) {
      setTimeout(() => setLocale(router?.query?.lang), 500);
    }
  }, [router.isReady, router?.query?.lang]);

  const sizesColumns = sizesTable?.[0]?.sizes?.map((size) => ({
    key: size?.key,
    title: size?.key,
  }));

  const sizesRows = sizesTable?.map((sizes) =>
    sizes.sizes.reduce((result, item) => {
      let key = Object.entries(item)[0][1]; //first property: a, b, c
      result[key] = item['value'];
      return result;
    }, {})
  );

  useEffect(() => {
    if (PERMISSIONS.IS_PRIVATE || isPublicDetail) {
      setLang(locale);
    } else {
      setLang(catalog?.lang || description || locale);
    }
  }, [
    PERMISSIONS.IS_PRIVATE,
    catalog?.lang,
    description,
    isPublicDetail,
    locale,
  ]);

  useEffect(() => {
    if (product?.code) {
      (async () => {
        const response = await dispatch(getCMSDetail(product.code));
        const _productBlocks =
          response.data?.items?.[0]?.body?.products?.[product.code];
        if (_productBlocks?.length) {
          setProductBlocks(_productBlocks);
        }
      })();
    }
  }, [dispatch, product?.code]);

  const getProduct = useCallback(async () => {
    const locales = new Set(['en_US', `${catalog?.lang || 'en_US'}`, locale]);
    const response = await request(
      `/api/product/export?ids=${id
        .split('-')
        .pop()}&acceptedlocales=en_US,it_IT,es_ES,fr_FR&currency=${
        catalog?.currency || 'EUR'
      }`,
      {
        method: 'GET',
      }
    );
    const [item] = response.data.items;
    setProduct(item);
  }, [catalog, id, locale]);

  useEffect(() => {
    if (catalog && locale && id && router.isReady) {
      const productId = Number(product?.id);
      const urlProductId = Number(id?.split('-')?.pop());
      if (
        isFirstRender ||
        (productId && urlProductId && productId !== urlProductId)
      ) {
        getProduct();
        setIsFirstRender(false);
      }
    }
  }, [
    catalog,
    getProduct,
    id,
    isFirstRender,
    locale,
    product?.id,
    router.isReady,
  ]);

  useEffect(() => {
    setTags(
      product?.tags.filter(
        (tag) => Object.keys(tag?.value?.translations || {}).length > 0
      )
    );
  }, [product?.tags]);

  const getRelatedSubcategory = useCallback(async () => {
    if (product?.attributes?.subcategory && product?.id) {
      const locales = new Set(['en_US', `${catalog?.lang || 'en_US'}`, locale]);
      let params = {
        page: 1,
        pageSize: 11,
        acceptedlocales: Array.from(locales)?.join(','),
        attributes: `subcategory-${product?.attributes?.subcategory}`,
        currency: catalog?.currency,
        availability: 1,
        gender: product.attributes.gender,
      };
      dispatch(
        getProducts(params, (data) => {
          setRelatedSubcategoryItems(
            data?.items.filter((item) => item.id !== product?.id)
          );
        })
      );
    }
  }, [
    catalog?.currency,
    catalog?.lang,
    dispatch,
    locale,
    product?.attributes?.gender,
    product?.attributes?.subcategory,
    product?.id,
  ]);

  const getRelatedBrand = useCallback(async () => {
    if (product?.attributes?.brand && product?.id) {
      const locales = new Set(['en_US', `${catalog?.lang || 'en_US'}`, locale]);
      let params = {
        page: 1,
        pageSize: 11,
        acceptedlocales: Array.from(locales)?.join(','),
        attributes: `brand-${encodeURIComponent(product?.attributes?.brand)}`,
        currency: catalog?.currency,
        availability: 1,
        gender: product.attributes.gender,
      };
      dispatch(
        getProducts(params, (data) => {
          setRelatedBrandItems(
            data?.items.filter((item) => item.id !== product?.id)
          );
        })
      );
    }
  }, [
    catalog?.currency,
    catalog?.lang,
    dispatch,
    locale,
    product?.attributes?.brand,
    product?.attributes?.gender,
    product?.id,
  ]);

  useEffect(() => {
    getRelatedSubcategory().then();
    getRelatedBrand().then();
  }, [getRelatedBrand, getRelatedSubcategory]);

  const retrieveModels = useCallback(
    (property) => {
      const items = {};
      product?.models?.forEach((model) => {
        items[model[property]] = {
          id: model.id,
          availability: model.availability,
        };
      });

      return Object.keys(items)
        .sort((a, b) => a - b)
        .map((item) => ({
          value: items[item].id,
          label: item,
          disabled: items[item].availability === 0,
        }));
    },
    [product?.models]
  );
  const models = product?.models;
  const sizes = useMemo(() => retrieveModels('size'), [retrieveModels]);
  const [model, setModel] = useState();
  const [quantity, setQuantity] = useState(1);
  const selectModel = useCallback(
    (model) => {
      const tempModel = product?.models?.find((el) => el.id === model?.value);
      setModel(tempModel);
      setSizeWarning(false);
      setQuantity(1);
    },
    [product?.models]
  );

  const singleSize =
    product?.models?.[0]?.size === 'NOSIZE' ||
    product?.models?.[0]?.size === 'UNI';

  useEffect(() => {
    if (
      !!sizes.length &&
      product?.models?.[0]?.availability > 0 &&
      singleSize
    ) {
      setModel(product?.models?.[0]);
      setQuantity(1);
    }
  }, [product?.models, singleSize, sizes.length]);

  useEffect(() => {
    if (product && model && singleSize) {
      TagManager.dataLayer({
        dataLayer: {
          event: 'view_item',
          content_ids: [`${model?.code}-${model?.id}`],
          content_type: 'product',
          content_name: product?.name,
          content_category: product?.attributes?.category,
          value: product?.taxable,
          currency: 'EUR',
          items: [
            {
              item_id: `${model?.code}-${model?.id}`,
              item_name: product?.name,
              price: product?.taxable,
              quantity: 1,
              item_brand: product?.brand,
              item_category2: product?.attributes?.category,
              item_category3: product?.attributes?.subcategory,
            },
          ],
        },
      });
    }
  }, [model, product, singleSize]);

  useEffect(() => {
    if (product && !singleSize) {
      TagManager.dataLayer({
        dataLayer: {
          event: 'view_item',
          content_ids: [
            model
              ? `${model?.code}-${model?.id}`
              : `${product?.code}-${product?.models?.[0]?.id}`,
          ],
          content_type: 'product',
          content_name: product?.name,
          content_category: product?.attributes?.category,
          value: product?.taxable,
          currency: 'EUR',
          items: [
            {
              item_id: model
                ? `${model?.code}-${model?.id}`
                : `${product?.code}-${product?.models?.[0]?.id}`,
              item_name: product?.name,
              price: product?.taxable,
              quantity: 1,
              item_brand: product?.brand,
              item_category2: product?.attributes?.category,
              item_category3: product?.attributes?.subcategory,
            },
          ],
        },
      });
    }
  }, [model, product, singleSize]);

  const handleLoginFree = useCallback(
    (redirectItem) => {
      const urlTags = [];
      if (redirectItem?.attributes?.brand) {
        urlTags.push(redirectItem?.attributes?.brand?.replace(/ /g, '-'));
      }
      if (redirectItem?.attributes?.subcategory) {
        urlTags.push(redirectItem?.attributes?.subcategory?.replace(/ /g, '-'));
      }
      urlTags.push(redirectItem?.code);
      urlTags.push(redirectItem?.id);
      router.push(
        `/login?redirectUrl=${encodeURIComponent(
          `/product/${urlTags.join('-')}`
        )}`,
        '/login'
      );
    },
    [router]
  );

  const productDescription = useMemo(() => {
    if (product?.description?.length && product?.description) {
      return { __html: product?.description };
    } else if (
      product?.descriptions?.[lang]?.length &&
      product?.descriptions?.[lang]
    ) {
      return { __html: product?.descriptions?.[lang] };
    } else if (
      product?.descriptions?.['en_US']?.length &&
      product?.descriptions?.['en_US']
    ) {
      return { __html: product?.descriptions?.['en_US'] };
    }
  }, [lang, product?.description, product?.descriptions]);

  const addProductToCart = useCallback(async () => {
    if (PERMISSIONS.IS_FREE_USER) {
      await router.push('/pricing');
    } else {
      if (!model) {
        selectModelRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
        setSizeWarning(true);
      } else {
        TagManager.dataLayer({
          dataLayer: {
            event: 'addToCart',
            price: model?.bestTaxable,
            currency: 'EUR',
            contents: [
              {
                id: model ? `${model?.code}-${model?.id}` : product?.code,
                quantity: quantity,
              },
            ],
            content_type: 'product',
            content_name: product?.name,
            content_category: product?.attributes?.category,
          },
        });
        dispatch(addProductCart({ ...product, model, quantity: quantity }));
        dispatch(setAddToCart(true));
        //setTimeout(() => dispatch(setAddToCart(false)), 5000);
      }
    }
  }, [PERMISSIONS.IS_FREE_USER, router, model, product, quantity, dispatch]);

  const onClickItem = useCallback(
    async (item) => {
      await router.push(
        `/product/${item?.brand}-${item?.attributes?.subcategory}-${item?.code}-${item?.id}`
      );
      setIsFirstRender(true);
    },
    [router]
  );

  const viewAllSubcategoryLink = useMemo(() => {
    const query = `attributes=subcategory-${product?.attributes?.subcategory}&availability=1`;
    return !PERMISSIONS.IS_FREE_USER && isLoggedIn && previousRoute?.catalogId
      ? `/channel/${previousRoute?.catalogId}/edit/products?${query}`
      : `/product?${query}`;
  }, [
    PERMISSIONS.IS_FREE_USER,
    isLoggedIn,
    previousRoute?.catalogId,
    product?.attributes?.subcategory,
  ]);

  const viewAllBrandLink = useMemo(() => {
    const query = `brand=${encodeURIComponent(
      product?.attributes?.brand
    )}&availability=1`;
    return !PERMISSIONS.IS_FREE_USER && isLoggedIn && previousRoute?.catalogId
      ? `/channel/${previousRoute?.catalogId}/edit/products?${query}`
      : `/product?${query}`;
  }, [
    PERMISSIONS.IS_FREE_USER,
    isLoggedIn,
    previousRoute?.catalogId,
    product?.attributes?.brand,
  ]);

  const breadcrumbs = useMemo(() => {
    const breadcrumbs = [];
    if (!product?.tags) {
      return breadcrumbs;
    }
    const { category, gender, subcategory, canale } = product.tags.reduce(
      (acc, tag) => {
        acc[tag.name] = {
          value: tag.value.value,
          label: tag.value.translations[locale] || tag.value.value,
        };
        return acc;
      },
      {}
    );

    const pathname = `${
      previousRoute?.catalogId
        ? `/channel/${previousRoute?.catalogId}/edit/products`
        : '/product'
    }`;
    const query = {
      availability: 1,
    };
    if (canale) {
      query.attributes = `canale-${canale.value}`;
      breadcrumbs.push({
        link: {
          pathname,
          query: {
            ...query,
          },
        },
        title: canale.label,
      });
    }

    if (gender) {
      query.gender = gender.value;
      breadcrumbs.push({
        link: {
          pathname,
          query: {
            ...query,
          },
        },
        title: gender.label,
      });
    }
    if (category) {
      query.category = category.value;
      breadcrumbs.push({
        link: {
          pathname,
          query: {
            ...query,
          },
        },
        title: category.label,
      });
    }
    if (subcategory) {
      query.subcategory = subcategory.value;
      breadcrumbs.push({
        link: {
          pathname,
          query: {
            ...query,
          },
        },
        title: subcategory.label,
      });
    }
    breadcrumbs.push({
      link: router?.asPath,
      title: product?.code,
    });

    return breadcrumbs;
  }, [
    locale,
    previousRoute?.catalogId,
    product?.code,
    product?.tags,
    router?.asPath,
  ]);

  const handleModalFree = useCallback(() => {
    setModalFreeOpen((prev) => !prev);
  }, []);

  const merchantMessage = ![
    '0',
    '25',
    '27',
    '26',
    '32',
    '8',
    '35',
    '42',
    '51',
    '54',
    '55',
    '62',
    '9',
    '74',
    '83',
    '89',
  ].includes(models?.[0]?.merchantId);
  const [showMessage, setShowMessage] = useState(false);

  const { userCashback } = useUserCashback(product?.attributes?.cashback);

  useEffect(() => {
    (async () => {
      if (product) {
        try {
          let response = await dispatch(
            getSizes({
              q: {
                brand: product?.attributes?.brand,
                category: product?.attributes?.category,
                gender: product?.attributes?.gender,
              },
            })
          );
          setSizesTable(response);
        } catch (e) {
          console.log('e', e);
        }
      }
    })();
  }, [dispatch, product]);

  const [modalOpen, setModalOpen] = useState({ type: null, index: -1 });
  const handleShare = useCallback(() => {
    if (!isLoggedIn) {
      setModalOpen({ type: 'share', index: 1 });
    } else {
      shareOnSocial();
    }
  }, [isLoggedIn, shareOnSocial]);
  const variants = product?.variants?.map((variant) => variant);

  const amountSaving = useMemo(() => {
    let amount = (product?.streetPrice - product?.bestTaxable).toFixed(2);
    if (amount <= 10) {
      return (product?.streetPrice - product?.bestTaxable).toFixed(2) + ' €';
    } else {
      return (
        (
          ((product?.streetPrice - product?.bestTaxable) /
            product?.streetPrice) *
          100
        ).toFixed(0) + '%'
      );
    }
  }, [product?.bestTaxable, product?.streetPrice]);

  const getProductName = useCallback(
    (p) =>
      `${
        p?.tags?.find((tag) => tag.name === 'subcategory')?.value
          ?.translations?.[locale] || ''
      } ${
        p?.tags?.find((tag) => tag.name === 'color')?.value?.translations?.[
          locale
        ] || ''
      } ${
        p?.tags?.find((tag) => tag.name === 'gender')?.value?.translations?.[
          locale
        ] || ''
      } ${p?.code}`,
    [locale]
  );

  const freeReturnMessage = useMemo(
    () =>
      router.isReady &&
      router.query.lang === 'it_IT' &&
      product?.attributes?.brand === 'Woolrich',
    [router.isReady, router.query.lang, product?.attributes?.brand]
  );

  useEffect(() => {
    if (!isLoggedIn && product?.code && !!referralCode) {
      const key = `${product?.code}${referralCode}`;
      const firstViewIds = JSON.parse(getLocalStorage('firstViewIds')) || [];
      const found = firstViewIds?.findIndex((id) => id === key);
      if (found === -1) {
        firstViewIds?.push(key);
        setLocalStorage('firstViewIds', JSON.stringify(firstViewIds));
        fetch(
          `${process.env.REPROXY_URL}/api/product/guest/${product?.code}?view=true&referralCode=${referralCode}`
        );
      }
    }
  }, [isLoggedIn, product?.code, referralCode]);

  const onClickH1 = useCallback(() => {
    const pathname = `${
      previousRoute?.catalogId
        ? `/channel/${previousRoute?.catalogId}/edit/products`
        : '/product'
    }`;
    router.push(
      `${pathname}?availability=1&gender=${product.attributes.gender}&brand=${product.attributes.brand}`
    );
  }, [
    previousRoute?.catalogId,
    product?.attributes?.brand,
    product?.attributes?.gender,
    router,
  ]);

  const addToCartButtonLabel = useMemo(() => {
    if (PERMISSIONS.IS_FREE_USER) {
      return TRANSLATIONS.products.detail.discoverOurPlans;
    } else if (model?.availability > 0 || !model) {
      return TRANSLATIONS.products.detail.ctaBtn;
    } else return TRANSLATIONS.products.detail.outOfStock;
  }, [
    PERMISSIONS.IS_FREE_USER,
    TRANSLATIONS.products.detail.ctaBtn,
    TRANSLATIONS.products.detail.discoverOurPlans,
    TRANSLATIONS.products.detail.outOfStock,
    model,
  ]);

  return (
    <>
      <Modal
        openModal={
          modalOpen?.type && (modalOpen?.index >= 0 || modalOpen?.index.length)
        }
        onClose={() => setModalOpen({ type: null, index: -1 })}
        width={isMobile ? '100%' : '400px'}
        scroll
        canClose
      >
        {modalOpen?.type === 'share' && (
          <div>
            <h3 className={'ta-c'}>{TRANSLATIONS.header?.modal?.title}</h3>
            <div className="mt-md mb-md ta-c">
              <p>
                Per poter guadagnare <strong>cahsback</strong> dagli acquisti
                tramite <strong>condivisione</strong> hai bisogno di avere un{' '}
                <strong>account</strong> Letsell
              </p>
            </div>
            <div className={'d-f fd-c g-10 ta-c jc-c mt-xl'}>
              <div>
                <Button
                  label={TRANSLATIONS?.header?.modal?.accedi}
                  className={'large primary'}
                  onClick={() => {
                    router.push('/login');
                  }}
                />
              </div>
              <div className={'d-f fd-c ai-c g-5 jc-c ta-c mb-xl'}>
                {TRANSLATIONS.shop.extraCash}
                <span
                  onClick={() => {
                    setModalOpen({ type: null, index: -1 });
                    shareOnSocial();
                  }}
                  className={'account'}
                >
                  {TRANSLATIONS.shop.shareProduct}
                </span>
              </div>
            </div>
          </div>
        )}
      </Modal>
      <Modal
        openModal={modalFreeOpen}
        onClose={handleModalFree}
        width={'400px'}
        title={TRANSLATIONS.cart.freeMessageTitle}
      >
        <div className="d-f fd-c">
          <p>{TRANSLATIONS.cart.freeMessage}</p>
          <p className="mt-md" style={{ fontWeight: 600, fontSize: 20 }}>
            {TRANSLATIONS.cart.freeMessageSubtitle}
          </p>
          <div className="d-f g-10 mt-xl">
            <Button
              className="primary wide large"
              label={TRANSLATIONS.cart.ctaPlan}
              onClick={handleLoginFree}
            />
          </div>
        </div>
      </Modal>
      <Modal
        openModal={sizesTableOpen}
        onClose={() => {
          setSizesTableOpen(false);
        }}
        width={`${isSmallDevice ? '100%' : '75%'}`}
        scroll
        title={TRANSLATIONS.products.detail.sizesTable}
      >
        <div className="custom-table">
          {!!sizesTable?.length ? (
            <Table columns={sizesColumns} data={sizesRows} />
          ) : (
            <div>{TRANSLATIONS.products.detail.noProducts}</div>
          )}
        </div>
      </Modal>
      {isMobile && <BannerShipping />}
      <div className={`product-detail ${isMobile && 'mb-xxl'}`}>
        <div className={'product-detail__top'}>
          <div className={'padding-breadcrumb'}>
            <Breadcrumb breadcrumbs={breadcrumbs} />
          </div>
          {product?.attributes?.brand === 'North Sails' && lang === 'it_IT' && (
            <div className="product_detail-preview-container">
              <div className="product_detail-preview-content">
                <p className="product_detail-1">
                  {TRANSLATIONS.products.detail.timer1}
                </p>
                <p className="product_detail-2">
                  {TRANSLATIONS.products.detail.timer2}
                </p>
                <Timer />
              </div>
            </div>
          )}
          <div className={`${!isMobile && 'row'} jc-sb`}>
            <div
              className={`col-xl-6 col-md-7 ${
                !!outOfStock && 'o5'
              } product-detail-sticky relative`}
            >
              <Swiper
                style={{
                  '--swiper-navigation-color': '#000',
                  '--swiper-pagination-color': '#000',
                }}
                zoom={true}
                autoHeight={true}
                autoplay={{
                  disableOnInteraction: true,
                  pauseOnMouseEnter: true,
                }}
                loop={false}
                spaceBetween={10}
                navigation={false}
                thumbs={{ swiper: thumbsSwiper }}
                modules={[
                  FreeMode,
                  Navigation,
                  Thumbs,
                  Zoom,
                  Pagination,
                  Autoplay,
                ]}
                className="swiper-detail"
              >
                {product?.pictures?.map((pic, index) => (
                  <SwiperSlide key={pic?.url}>
                    <div className="swiper-zoom-container">
                      <img
                        src={pic.url ? getFileAddress(pic) : pic?.url}
                        alt={`${
                          product?.tags?.find((tag) => tag.name === 'brand')
                            ?.value?.translations?.[locale] ||
                          product?.attributes?.brand
                        } - ${getProductName(product)} -${index + 1} | BDroppy`}
                      />
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
              <Swiper
                onSwiper={setThumbsSwiper}
                loop={false}
                spaceBetween={!isMobile ? 10 : 6}
                slidesPerView={4}
                freeMode={true}
                watchSlidesProgress={true}
                pagination={{ type: isMobile && 'progressbar' }}
                modules={[FreeMode, Navigation, Thumbs, Pagination]}
                className="thumbnails"
              >
                {product?.pictures?.map((pic, index) => (
                  <SwiperSlide key={pic?.url}>
                    <img
                      src={pic.url ? getFileAddress(pic) : pic?.url}
                      alt={`${
                        product?.tags?.find((tag) => tag.name === 'brand')
                          ?.value?.translations?.[locale] ||
                        product?.attributes?.brand
                      } - ${getProductName(product)} -${index + 1} | BDroppy`}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
              {false && product?.attributes?.discountpercentage && (
                <div className="flag-discount">
                  <p>{TRANSLATIONS.products.list.extradiscount}</p>
                </div>
              )}
              <div
                style={{
                  background: 'rgba(255,255,255,.7)',
                  width: 35,
                  height: 35,
                  borderRadius: 100,
                  border: 'solid thin #ccc8',
                  position: 'absolute',
                  top: 14,
                  right: 22,
                  zIndex: 1,
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <InlineIcon
                  icon={
                    isIOS || isSafari
                      ? 'ic:outline-ios-share'
                      : 'ic:round-share'
                  }
                  style={{
                    color: 'black',
                    fontSize: 22,
                    cursor: 'pointer',
                  }}
                  onClick={shareOnSocial}
                />
              </div>
            </div>
            <div className="col-xl-5 col-md-5">
              <div className="product-info">
                <div className="padding-md d-f jc-sb mt-md mb-md g-5">
                  <div className="d-f fd-c g-10">
                    {product?.attributes?.producttitle &&
                      locale === 'it_IT' && (
                        <span>{product?.attributes?.brand}</span>
                      )}
                    <h1
                      className={`fw-w fd-c g-5 d-f ${
                        isMobile && 'mt-xl'
                      } detail-brand`}
                    >
                      <div
                        onClick={onClickH1}
                        style={{
                          cursor: 'pointer',
                          textDecoration: 'underline',
                        }}
                      >
                        {product?.attributes?.producttitle && locale === 'it_IT'
                          ? product?.attributes?.producttitle
                          : product?.tags?.find((tag) => tag.name === 'brand')
                              ?.value?.translations?.[locale] ||
                            product?.tags?.find((tag) => tag.name === 'brand')
                              ?.value?.translations?.['it_IT'] ||
                            product?.attributes?.brand}
                      </div>
                      {!product?.attributes?.producttitle && (
                        <span>
                          {`${
                            product?.tags?.find(
                              (tag) => tag.name === 'subcategory'
                            )?.value?.translations?.[locale] || ''
                          }`}{' '}
                          {`${
                            product?.tags?.find((tag) => tag.name === 'color')
                              ?.value?.translations?.[locale] || ''
                          }`}{' '}
                          {`${
                            product?.tags?.find((tag) => tag.name === 'gender')
                              ?.value?.translations?.[locale] || ''
                          }`}
                        </span>
                      )}
                      <span className="mb-20">{product?.code}</span>
                    </h1>
                  </div>
                </div>
                <div className="padding-md col-xl-12">
                  <div className="d-f fd-c g-5 mt-md mb-md">
                    <div className={'price-details__cost d-f jc-sb g-5 ai-c'}>
                      <div className={`d-f ai-c g-5`}>
                        <h4>
                          <strong>
                            {product?.bestTaxable?.toFixed(2)} {currencySymbol}
                          </strong>
                        </h4>
                        {product?.streetPrice > 0 && (
                          <div className={'d-f g-5 detail-retail-price'}>
                            <p className={'l-t'}>
                              {product?.streetPrice?.toFixed(2)}{' '}
                              {currencySymbol}
                            </p>
                          </div>
                        )}
                      </div>
                      {product?.streetPrice > 0 && (
                        <div className={'d-f g-5 ai-c'}>
                          <span className={'save'} style={{ fontSize: 14 }}>
                            {TRANSLATIONS.products.detail.save} {amountSaving}
                          </span>
                          <Tooltip
                            className="left"
                            text={TRANSLATIONS.products.detail.saveInfo}
                          >
                            <div className="d-f ai-c jc-c icon-detail">
                              <Icon
                                type="info"
                                style={{
                                  fontSize: 15,
                                  color: 'var(--primary)',
                                }}
                              />
                            </div>
                          </Tooltip>
                        </div>
                      )}
                    </div>
                  </div>
                  {product?.attributes?.sales === 'reductions' && (
                    <div className="price-details__retail d-f fd-c g-5">
                      <div className={'fw-w d-f'}>
                        <div className={'black-friday  d-f g-5'}>
                          <span>{TRANSLATIONS.products.detail.discount}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className={`d-f fd-c g-10`}>
                  {(!!variants?.length || sizes?.length > 1 || !singleSize) &&
                    !PERMISSIONS.IS_FREE_USER && (
                      <div className={'detail-wrapper d-f jc-sb g-10 fd-c'}>
                        {!!variants?.length && (
                          <div className={'color-variant d-f fd-c fw-w f-1'}>
                            <p className={''}>
                              {TRANSLATIONS.products.detail.color}
                            </p>
                            <VariantSelector variants={variants} />
                          </div>
                        )}
                        {(sizes?.length > 1 || !singleSize) && (
                          <div
                            ref={selectModelRef}
                            className={'size-input d-f f-1 fd-c'}
                          >
                            <div className={'d-f jc-sb ai-c mb-md'}>
                              <p style={{ textTransform: 'uppercase' }}>
                                {TRANSLATIONS.products.detail.size}
                              </p>
                            </div>
                            <SizeSelector
                              name="model"
                              value={model?.id}
                              options={sizes}
                              defaultValue={model?.id}
                              clearable={false}
                              placeholder={TRANSLATIONS.products.detail.select}
                              onChange={selectModel}
                              sizesMapping={sizesTable.map(
                                (size) => size.sizes
                              )}
                            />
                            {sizeWarning && (
                              <p style={{ color: 'red', marginTop: '8px' }}>
                                {TRANSLATIONS.cart.warningNoSize}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  {PERMISSIONS.IS_FREE_USER && (
                    <div
                      style={{
                        borderRadius: 10,
                        border: '1px solid #005eff',
                        background: '#f0f8fe',
                        marginTop: 15,
                      }}
                    >
                      <div className={'info-wrapper'}>
                        <ul className={'d-f fd-c g-10'}>
                          <li className={'d-f'}>
                            <div
                              style={{
                                minWidth: '30px',
                                display: 'flex',
                                alignItems: 'center',
                              }}
                            >
                              <Icon type="info" />
                            </div>
                            <div className={'d-f fd-c li-detail'}>
                              <p>{TRANSLATIONS.products.detail.freeAlert}</p>
                            </div>
                          </li>
                        </ul>
                      </div>
                    </div>
                  )}
                  <div className="detail-wrapper d-f g-10 jc-sb">
                    {!PERMISSIONS.IS_FREE_USER && (
                      <div className={'size-input f-1 d-f'}>
                        <Counter
                          initialNumber={quantity}
                          maximum={model?.availability}
                          minimum={1}
                          onChange={setQuantity}
                          disabled={!model?.availability}
                        />
                      </div>
                    )}
                    <div className="d-f f-2">
                      <Button
                        label={addToCartButtonLabel}
                        className={'wide large gradient-blue radius-xl'}
                        onClick={addProductToCart}
                        disabled={
                          PERMISSIONS.IS_FREE_USER
                            ? false
                            : !model?.availability
                        }
                      />
                    </div>
                  </div>
                  {/*
                     <div className="price-details__retail d-f fd-c g-5 mb-md cashback-wrapper">
                    {!!userCashback && (
                      <div className={'fw-w d-f'}>
                        <div className={'cashback  d-f g-5'}>
                          <span>
                            {translationsParameters(
                              TRANSLATIONS.products.detail.youCanEarn,
                              [
                                <span
                                  className={'cashback-amount'}
                                  key="cashback-amount"
                                >
                                  {(
                                    (userCashback * product?.bestTaxable) /
                                    100
                                  ).toFixed(2)}{' '}
                                  {currencySymbol}{' '}
                                  <span
                                    style={{
                                      fontWeight: 500,
                                      fontSize: 16,
                                    }}
                                  >
                                    {TRANSLATIONS.products.detail.ofCashback}
                                  </span>
                                </span>,
                              ]
                            )}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                     */}
                </div>
                <div className="padding-md col-xl-12 mt-xl">
                  <div ref={refContent}>
                    <div
                      className={`${open && !isIOS && 'content_read_more'} ${
                        open && isIOS && 'content_read_more_ios'
                      } content_default`}
                    >
                      {product &&
                      Object.keys(product?.descriptions)?.length !== 0 ? (
                        <div dangerouslySetInnerHTML={productDescription} />
                      ) : (
                        <>
                          {product?.tags?.map((tag, index) => (
                            <p key={index}>
                              <span>{tag.name}</span>: {tag.value.value}
                            </p>
                          ))}
                        </>
                      )}
                    </div>
                  </div>
                  <div
                    className={`d-f ai-c jc-c g-10 ${
                      open ? 'read-more-span' : 'read-less-span'
                    } mt-md mb-md`}
                    onClick={handleOpen}
                    ref={refReadMore}
                  >
                    <span>
                      {open
                        ? TRANSLATIONS.products.detail.readMore
                        : TRANSLATIONS.products.detail.readLess}
                    </span>
                    <Icon
                      type={`${open ? 'chevron-down' : 'chevron-up'}`}
                      style={{ cursor: 'pointer' }}
                    />
                  </div>
                </div>
                <div className="detail-background_2">
                  <div className={'info-wrapper'}>
                    <ul className={'d-f fd-c g-10'}>
                      <li className={'d-f g-5'}>
                        <div style={{ minWidth: '41px' }}>
                          <img
                            src={
                              '../images/icon/svg/Delivery, Truck, Fast 2.svg'
                            }
                            alt={'Delivery, Truck, Fast 2'}
                          />
                        </div>
                        <div className={'d-f fd-c li-detail'}>
                          <p>{TRANSLATIONS.products.detail.spedizioni}</p>
                          <span>{TRANSLATIONS.products.detail.gratis}</span>
                        </div>
                      </li>
                      <li className={'d-f g-5'}>
                        <div style={{ minWidth: '41px' }}>
                          <img
                            src={
                              '../images/icon/svg/hand-package-delivery 1.svg'
                            }
                            alt={'hand-package-delivery 1'}
                          />
                        </div>
                        {freeReturnMessage ? (
                          <div className={'d-f fd-c li-detail'}>
                            <p>{TRANSLATIONS.products.detail.reso_free}</p>
                            <span>
                              {TRANSLATIONS.products.detail.reso_p_free}
                            </span>
                          </div>
                        ) : (
                          <div className={'d-f fd-c li-detail'}>
                            <p>{TRANSLATIONS.products.detail.reso}</p>
                            <span>{TRANSLATIONS.products.detail.reso_p}</span>
                          </div>
                        )}
                      </li>
                      <li className={'d-f g-5'}>
                        <div style={{ minWidth: '41px' }}>
                          <img
                            src={'../images/icon/svg/hand-money-banknote 3.svg'}
                            alt={'hand-money-banknote 3'}
                          />
                        </div>
                        <div className={'d-f fd-c li-detail'}>
                          <p>{TRANSLATIONS.products.detail.pagamenti}</p>
                          <span>
                            {TRANSLATIONS.products.detail.pagamenti_p}
                          </span>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="mt-md padding-text">
                  {merchantMessage && (
                    <div className="vat-it relative mb-xl d-f fd-c g-10">
                      <div className={'d-f ai-c g-5'}>
                        <p style={{ fontSize: 14, fontWeight: 500 }}>
                          {TRANSLATIONS.products.list.partner}
                        </p>{' '}
                        <Icon
                          type="info"
                          style={{
                            cursor: 'pointer',
                          }}
                          className="alert-icon"
                          onClick={() => {
                            setShowMessage((prevState) => !prevState);
                          }}
                        />
                      </div>
                      {showMessage && (
                        <div className={'vat-it-info relative'}>
                          <Icon
                            style={{
                              position: 'absolute',
                              fontSize: 18,
                              right: 8,
                              top: 8,
                              cursor: 'pointer',
                            }}
                            type={'x'}
                            onClick={() => {
                              setShowMessage(false);
                            }}
                          />
                          <span style={{ fontSize: 13 }}>
                            {TRANSLATIONS.products.list.info}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {PERMISSIONS.IS_ONLY_SOCIAL && false && (
                  <div className={'share-box relative'}>
                    <div className={'d-f g-20 ai-c'}>
                      <div>
                        <img
                          src={'../images/icon/svg/friends.svg'}
                          width={100}
                          alt={'friends'}
                        />
                      </div>
                      <div className={'d-f fd-c g-5'}>
                        <span className={'share-title'}>
                          {TRANSLATIONS.products.detail.shareAndEarn}
                        </span>
                        <span className={'share-span'}>
                          {TRANSLATIONS.products.detail.copy}
                        </span>
                        <span
                          className={'share-cta'}
                          onClick={() =>
                            setModalOpen({
                              type: 'condividi',
                              index: 1,
                            })
                          }
                          style={{ cursor: 'pointer' }}
                        >
                          {TRANSLATIONS.products.detail.scopri}
                        </span>
                      </div>
                    </div>
                    <div className={'share-circle d-f jc-c ai-c'}>
                      <Icon
                        type={'share-2'}
                        style={{
                          color: 'white',
                          fontSize: 24,
                          cursor: 'pointer',
                        }}
                        onClick={handleShare}
                      />
                    </div>
                  </div>
                )}
                {!isMobile && (
                  <>
                    <div className={'margin-accordion detail'}>
                      <Accordion label={TRANSLATIONS.products.detail.question}>
                        <div className={'detail__accordion'}>
                          {TRANSLATIONS.products.detail.answer}
                        </div>
                      </Accordion>
                      <Accordion
                        label={TRANSLATIONS.products.detail.question_1}
                      >
                        <div className={'detail__accordion'}>
                          {TRANSLATIONS.products.detail.answer_1}
                        </div>
                      </Accordion>
                      <Accordion
                        label={TRANSLATIONS.products.detail.question_2}
                      >
                        <div className={'detail__accordion'}>
                          {TRANSLATIONS.products.detail.answer_2}
                        </div>
                      </Accordion>
                    </div>
                    <div className={'payment-method payment-margin relative'}>
                      <div className={'d-f fd-c g-20 ai-c'}>
                        <span>
                          {TRANSLATIONS.products.detail.securePayment}
                        </span>
                        <div className={'d-f jc-sb ai-c g-30'}>
                          <img
                            src={'../images/icon/svg/Group 470.png'}
                            width={50}
                            alt={'Group 470'}
                          />
                          <img
                            src={'../images/icon/svg/image 102.png'}
                            width={50}
                            alt={'image 102'}
                          />
                          <img
                            src={'../images/icon/svg/image 109.png'}
                            width={50}
                            alt={'image 109'}
                          />
                          <img
                            src={'../images/icon/svg/Group 469.svg'}
                            width={55}
                            alt={'Group 469'}
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        {!!productBlocks?.length &&
          locale === 'it_IT' &&
          productBlocks?.map((block, index) => (
            <div
              className={`detail-background-padding ${!isMobile && 'mt-xxl'}`}
              style={{ background: `${block.background}` }}
              key={index}
            >
              <div className={'detail-subcategory-content'}>
                <div
                  className={`d-f jc-sb ai-c g-30 padding-md ${
                    block.flexReverse && 'fd-rr'
                  } ${isMobile && 'fd-c'}`}
                >
                  {block?.img && (
                    <div className={`d-f f-1 ${isMobile && 'w-100'} jc-c`}>
                      <img
                        src={block?.img}
                        alt={'detail subcategory'}
                        style={{ maxWidth: 500 }}
                      />
                    </div>
                  )}
                  <div className={'f-1 d-f fd-c g-5'}>
                    <span className={'block-subTitle'}>{block.subTitle}</span>
                    <h3 className={'block-title fw-w d-f'}>{block.title}</h3>
                    <p className={'block-desc mt-md'}>{block.desc}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        {isMobile && (
          <>
            <div className={'margin-accordion detail'}>
              <Accordion label={TRANSLATIONS.products.detail.question}>
                <div className={'detail__accordion'}>
                  {TRANSLATIONS.products.detail.answer}
                </div>
              </Accordion>
              <Accordion label={TRANSLATIONS.products.detail.question_1}>
                <div className={'detail__accordion'}>
                  {TRANSLATIONS.products.detail.answer_1}
                </div>
              </Accordion>
              <Accordion label={TRANSLATIONS.products.detail.question_2}>
                <div className={'detail__accordion'}>
                  {TRANSLATIONS.products.detail.answer_2}
                </div>
              </Accordion>
            </div>

            <div className={'payment-method payment-margin relative'}>
              <div className={'d-f fd-c g-20 ai-c'}>
                <span>{TRANSLATIONS.products.detail.securePayment}</span>
                <div className={'d-f jc-sb ai-c g-30'}>
                  <img
                    src={'../images/icon/svg/Group 470.png'}
                    width={50}
                    alt={'Group 470'}
                  />
                  <img
                    src={'../images/icon/svg/image 102.png'}
                    width={50}
                    alt={'image 102'}
                  />
                  <img
                    src={'../images/icon/svg/image 109.png'}
                    width={50}
                    alt={'image 109'}
                  />
                  <img
                    src={'../images/icon/svg/cash-on-delivery.svg'}
                    width={50}
                    alt={'cash-on-delivery'}
                  />
                </div>
              </div>
            </div>
          </>
        )}
        {!!relatedBrandItems?.length && (
          <div
            className={`detail-background detail-background-padding ${
              !isMobile && 'mt-xxl'
            }`}
          >
            <div className="detail-subcategory-content">
              <div className="d-f fd-r jc-sb ai-c padding-text">
                <h4>{TRANSLATIONS.products.detail.sameBrand}</h4>
                <Link href={viewAllBrandLink}>
                  <div className="view-all-link">
                    {TRANSLATIONS.products.detail.viewAll}
                    <Icon type="arrow-right" />
                  </div>
                </Link>
              </div>
              <Swiper
                spaceBetween={!isMobile && 6}
                slidesPerGroup={1}
                className="swiper-subcategory"
                slidesPerView={'auto'}
                loopFillGroupWithBlank={true}
                freeMode={true}
                navigation={!isMobile}
                modules={[
                  Pagination,
                  Autoplay,
                  Navigation,
                  FreeMode,
                  HashNavigation,
                ]}
                grabCursor={true}
              >
                {relatedBrandItems?.map((item) => (
                  <SwiperSlide key={item?.id}>
                    <div
                      className="swiper-grab-container"
                      onClick={() => onClickItem(item)}
                    >
                      <img
                        style={{ border: '1px solid #f1f1f1' }}
                        src={
                          item?.pictures?.[0]?.url
                            ? getFileAddress(item?.pictures?.[0])
                            : item?.pictures?.[0]?.url
                        }
                        alt={`${
                          item?.tags?.find((tag) => tag.name === 'brand')?.value
                            ?.translations?.[locale] || item?.attributes?.brand
                        } - ${getProductName(item)} -1 | BDroppy`}
                      />
                      <span className="related-description">
                        {item?.tags?.find((tag) => tag.name === 'brand')?.value
                          ?.translations?.[locale] || item?.attributes?.brand}
                      </span>
                      <span className="related-description">
                        {item?.tags?.find((tag) => tag.name === 'subcategory')
                          ?.value?.translations?.[locale] ||
                          item?.tags?.find((tag) => tag.name === 'subcategory')
                            ?.value?.translations?.['en_US'] ||
                          item?.tags?.find((tag) => tag.name === 'subcategory')
                            ?.value?.value}
                      </span>
                      <span className="related-taxable">
                        {item?.bestTaxable} {currencySymbol}
                      </span>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </div>
        )}
        {!!relatedSubcategoryItems?.length && (
          <div className="detail-brand-content detail-background-padding">
            <div className="d-f fd-r jc-sb ai-c padding-text">
              <h4>{TRANSLATIONS.products.detail.similarProducts}</h4>
              <Link href={viewAllSubcategoryLink}>
                <div className="view-all-link">
                  {TRANSLATIONS.products.detail.viewAll}
                  <Icon type="arrow-right" />
                </div>
              </Link>
            </div>
            <Swiper
              spaceBetween={!isMobile && 6}
              slidesPerGroup={1}
              className="swiper-subcategory"
              slidesPerView={'auto'}
              loopFillGroupWithBlank={true}
              freeMode={true}
              navigation={!isMobile}
              modules={[
                Pagination,
                Autoplay,
                Navigation,
                FreeMode,
                HashNavigation,
              ]}
              grabCursor={true}
            >
              {relatedSubcategoryItems?.map((item) => (
                <SwiperSlide key={item?.id}>
                  <div
                    className="swiper-grab-container"
                    onClick={() => onClickItem(item)}
                  >
                    <img
                      src={
                        item?.pictures?.[0]?.url
                          ? getFileAddress(item?.pictures?.[0])
                          : item?.pictures?.[0]?.url
                      }
                      alt={`${
                        item?.tags?.find((tag) => tag.name === 'brand')?.value
                          ?.translations?.[locale] || item?.attributes?.brand
                      } - ${getProductName(item)} -1 | BDroppy`}
                    />
                    <span className="related-description">
                      {item?.tags?.find((tag) => tag.name === 'brand')?.value
                        ?.translations?.[locale] || item?.attributes?.brand}
                    </span>
                    <span className="related-description">
                      {item?.tags?.find((tag) => tag.name === 'subcategory')
                        ?.value?.translations?.[locale] ||
                        item?.tags?.find((tag) => tag.name === 'subcategory')
                          ?.value?.translations?.['en_US'] ||
                        item?.tags?.find((tag) => tag.name === 'subcategory')
                          ?.value?.value}
                    </span>
                    <span className="related-taxable">
                      {item?.bestTaxable} {currencySymbol}
                    </span>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        )}
        <style jsx>{style}</style>
      </div>
    </>
  );
}
